import React, { useEffect, useMemo, useState } from 'react';
import { Organization } from '../../types/organization';
import { Package, OrganizationSubscription } from '../../types/package';
import { packageService } from '../../services/packageService';
import { organizationService } from '../../services/organizationService';
import { Switch } from '../common/Switch';
import { useNavigate } from 'react-router-dom';
import { ArrowTopRightOnSquareIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import DateTimePicker from '../common/DateTimePicker';
import { db } from '../../lib/firebase';
import { doc, getDoc } from 'firebase/firestore';
import toast from 'react-hot-toast';

interface Props {
  organization: Organization;
  onOrganizationUpdate: (org: Organization) => void;
  onRequestDelete?: (org: Organization) => void;
}

const STATUS: { id: OrganizationSubscription['status']; label: string; chip: string }[] = [
  { id: 'trial', label: 'Próbaidőszak', chip: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
  { id: 'active', label: 'Aktív', chip: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' },
  { id: 'suspended', label: 'Felfüggesztve', chip: 'bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' },
  { id: 'inactive', label: 'Inaktív', chip: 'bg-gray-100 text-gray-700 dark:bg-gray-700/60 dark:text-gray-200' },
  { id: 'cancelled', label: 'Lemondva', chip: 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300' }
];
const toInput = (d?: Date | null) => (d && !isNaN(d.getTime()) ? d.toISOString().slice(0, 10) : '');
const fromInput = (v: string) => (v ? new Date(v) : undefined);
const anyDate = (v: any): Date | null => { if (!v) return null; const d = v?.toDate ? v.toDate() : v instanceof Date ? v : new Date(v); return isNaN(d.getTime()) ? null : d; };
const money = (n: number, c = 'HUF') => new Intl.NumberFormat('hu-HU', { style: 'currency', currency: c, maximumFractionDigits: 0 }).format(n || 0);

/** Szuper-admin: a szervezet előfizetése, csomagja és rendszerállapota. */
const OrganizationSubscriptionTab: React.FC<Props> = ({ organization, onOrganizationUpdate, onRequestDelete }) => {
  const navigate = useNavigate();
  const [packages, setPackages] = useState<Package[]>([]);
  const [subs, setSubs] = useState<OrganizationSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [owner, setOwner] = useState<{ name: string; email: string } | null>(null);
  useEffect(() => {
    if (!organization.ownerId) return;
    getDoc(doc(db, 'users', organization.ownerId)).then(snap => { if (snap.exists()) { const u = snap.data(); setOwner({ name: u.displayName || [u.firstName, u.lastName].filter(Boolean).join(' ') || '', email: u.email || '' }); } }).catch(() => {});
  }, [organization.ownerId]);
  const [form, setForm] = useState({ packageId: '', status: 'trial' as OrganizationSubscription['status'], startDate: toInput(new Date()), endDate: '', trialEndDate: '', autoRenew: true });

  const current = useMemo(() => subs.find(s => s.status === 'active' || s.status === 'trial') || subs[0], [subs]);
  const pkg = useMemo(() => packages.find(p => p.id === (form.packageId || current?.packageId)), [packages, form.packageId, current]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [p, s] = await Promise.all([packageService.getAllPackages(), packageService.getSubscriptionsByOrganization(organization.id)]);
        if (cancelled) return;
        setPackages(p); setSubs(s);
        const cur = s.find(x => x.status === 'active' || x.status === 'trial') || s[0];
        if (cur) setForm({ packageId: cur.packageId, status: cur.status, startDate: toInput(cur.startDate), endDate: toInput(cur.endDate || null), trialEndDate: toInput(cur.trialEndDate || null), autoRenew: cur.autoRenew !== false });
        else if (p[0]) setForm(f => ({ ...f, packageId: p[0].id }));
      } catch (e) { console.error(e); toast.error('Az előfizetés betöltése nem sikerült'); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [organization.id]);

  const save = async () => {
    if (!form.packageId) { toast.error('Válassz csomagot'); return; }
    try {
      setSaving(true);
      const data = {
        packageId: form.packageId, status: form.status,
        startDate: new Date(form.startDate || Date.now()),
        endDate: form.endDate ? new Date(form.endDate) : undefined,
        trialEndDate: form.trialEndDate ? new Date(form.trialEndDate) : undefined,
        autoRenew: form.autoRenew
      };
      if (current) {
        await packageService.updateSubscription(current.id, data);
      } else {
        await packageService.createSubscription({ ...data, organizationId: organization.id, usage: { users: (organization.users?.length || 0) + 1, storage: 0, apiCalls: 0, lastUpdated: new Date() } } as any);
      }
      const s = await packageService.getSubscriptionsByOrganization(organization.id);
      setSubs(s);
      toast.success('Előfizetés mentve');
    } catch (e: any) { toast.error(e?.message || 'Mentés sikertelen'); }
    finally { setSaving(false); }
  };

  const toggleActive = async (v: boolean) => {
    try {
      await organizationService.updateOrganization(organization.id, { isActive: v } as any);
      onOrganizationUpdate({ ...organization, isActive: v });
      toast.success(v ? 'Szervezet aktiválva' : 'Szervezet felfüggesztve');
    } catch { toast.error('Nem sikerült menteni'); }
  };

  if (loading) return <div className="text-sm text-gray-500">Betöltés…</div>;
  const st = STATUS.find(x => x.id === (current?.status || form.status));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div className="crm-card px-3 py-2"><div className="text-[11px] text-gray-500 dark:text-gray-400">Csomag</div><div className="text-sm font-semibold truncate">{pkg?.name || '–'}</div></div>
        <div className="crm-card px-3 py-2"><div className="text-[11px] text-gray-500 dark:text-gray-400">Státusz</div><div className="text-sm">{st ? <span className={`inline-flex items-center px-2 h-5 rounded-full text-[11px] font-medium ${st.chip}`}>{st.label}</span> : '–'}</div></div>
        <div className="crm-card px-3 py-2"><div className="text-[11px] text-gray-500 dark:text-gray-400">Havi díj</div><div className="text-sm font-semibold tabular-nums">{pkg ? money(pkg.price.monthly, pkg.price.currency) : '–'}</div></div>
        <div className="crm-card px-3 py-2"><div className="text-[11px] text-gray-500 dark:text-gray-400">Felhasználók</div><div className="text-sm font-semibold tabular-nums">{(organization.users?.length || 0) + 1}{pkg?.maxUsers ? ` / ${pkg.maxUsers}` : ''}</div></div>
      </div>

      {pkg?.maxUsers && (organization.users?.length || 0) + 1 > pkg.maxUsers && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-800 dark:text-amber-300"><ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />A szervezet túllépte a csomag felhasználói limitjét.</div>
      )}

      <div className="crm-card p-4 space-y-3">
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Előfizetés</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="text-xs text-gray-600 dark:text-gray-300">Csomag
            <select value={form.packageId} onChange={e => setForm({ ...form, packageId: e.target.value })} className="mt-1 w-full h-9 pl-3 pr-8 text-sm border rounded-lg">
              {packages.map(p => <option key={p.id} value={p.id}>{p.name} – {money(p.price.monthly, p.price.currency)}/hó{p.isActive === false ? ' (inaktív)' : ''}</option>)}
            </select>
          </label>
          <label className="text-xs text-gray-600 dark:text-gray-300">Státusz
            <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value as any })} className="mt-1 w-full h-9 pl-3 pr-8 text-sm border rounded-lg">
              {STATUS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </label>
          <div className="text-xs text-gray-600 dark:text-gray-300">Kezdés<div className="mt-1"><DateTimePicker value={fromInput(form.startDate)} onChange={d => setForm({ ...form, startDate: toInput(d || null) })} placeholder="Kezdés dátuma" /></div></div>
          <div className="text-xs text-gray-600 dark:text-gray-300">Lejárat (üres = határozatlan)<div className="mt-1"><DateTimePicker value={fromInput(form.endDate)} onChange={d => setForm({ ...form, endDate: toInput(d || null) })} placeholder="Nincs lejárat" /></div></div>
          {form.status === 'trial' && <div className="text-xs text-gray-600 dark:text-gray-300">Próbaidőszak vége<div className="mt-1"><DateTimePicker value={fromInput(form.trialEndDate)} onChange={d => setForm({ ...form, trialEndDate: toInput(d || null) })} placeholder="Próbaidőszak vége" /></div></div>}
          <label className="flex items-center gap-2 text-sm self-end h-9"><Switch checked={form.autoRenew} onChange={v => setForm({ ...form, autoRenew: v })} size="sm" />Automatikus megújítás</label>
        </div>
        {pkg && (
          <div className="text-[11px] text-gray-500 dark:text-gray-400">Csomag tartalma: {pkg.maxUsers} felhasználó · {pkg.maxStorage} GB tárhely · {pkg.modules?.length || 0} modul{pkg.features?.length ? ` · ${pkg.features.slice(0, 4).join(', ')}${pkg.features.length > 4 ? '…' : ''}` : ''}</div>
        )}
        <div className="flex items-center justify-between pt-1">
          <button type="button" onClick={() => navigate('/system-admin/subscriptions')} className="text-xs text-primary-600 hover:underline inline-flex items-center gap-1">Összes előfizetés (rendszeradmin)<ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" /></button>
          <button type="button" onClick={save} disabled={saving} className="crm-btn crm-btn-primary h-8 text-xs">{saving ? 'Mentés…' : current ? 'Előfizetés frissítése' : 'Előfizetés létrehozása'}</button>
        </div>
      </div>

      {current && (
        <div className="text-[11px] text-gray-500 dark:text-gray-400 space-y-0.5">
          <div>Használat: {current.usage?.users ?? '–'} felhasználó · {current.usage?.storage ?? 0} GB · {current.usage?.apiCalls ?? 0} API hívás</div>
          {current.lastPaymentDate && <div>Utolsó fizetés: {current.lastPaymentDate.toLocaleDateString('hu-HU')}{current.nextPaymentDate ? ` · következő: ${current.nextPaymentDate.toLocaleDateString('hu-HU')}` : ''}</div>}
          {subs.length > 1 && <div>Korábbi előfizetések: {subs.length - 1}</div>}
        </div>
      )}

      <div className="crm-card p-4 space-y-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Rendszer</div>
        <div className="flex items-center justify-between text-sm">
          <div><div className="font-medium">Szervezet aktív</div><div className="text-xs text-gray-500 dark:text-gray-400">Kikapcsolva a tagok nem tudják használni a szervezetet.</div></div>
          <Switch checked={organization.isActive !== false} onChange={toggleActive} size="sm" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 text-xs text-gray-500 dark:text-gray-400 pt-1">
          <div>Azonosító: <span className="font-mono text-gray-700 dark:text-gray-200">{organization.id}</span></div>
          <div>Tulajdonos: <span className="text-gray-700 dark:text-gray-200">{owner ? `${owner.name || owner.email}${owner.name && owner.email ? ` (${owner.email})` : ''}` : organization.ownerId}</span></div>
          <div>Létrehozva: {anyDate(organization.createdAt)?.toLocaleDateString('hu-HU') || '–'}</div>
          <div>Iparág: {organization.industry || '–'}</div>
        </div>
      </div>

      {onRequestDelete && (
        <div className="rounded-xl border border-rose-200 dark:border-rose-800 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="text-sm flex-1"><div className="font-medium text-rose-700 dark:text-rose-300">Szervezet törlése</div><div className="text-xs text-gray-500 dark:text-gray-400">Végleges. Névmegerősítéssel, opcionálisan minden adatával együtt.</div></div>
          <button type="button" onClick={() => onRequestDelete(organization)} className="crm-btn crm-btn-danger">Törlés…</button>
        </div>
      )}
    </div>
  );
};

export default OrganizationSubscriptionTab;
